import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Sparkles, Compass, Hammer, Settings, ArrowRightLeft, ArrowLeft,
    FileText, Layers, Users2, BookOpenCheck, Target,
    Building, Scale, UserPlus, HeartHandshake, Briefcase, LifeBuoy, Megaphone, ServerCog, Shuffle,
    GraduationCap, Cpu, TrendingUp,
    ClipboardList, FileSearch, Repeat2, CheckCircle2
} from "lucide-react";
import "./GccBuildServices.css";

const tabs = [
    { key: "plan", label: "Plan Services", icon: Compass, blurb: "Lay the foundation of a future-ready, innovative GCC." },
    { key: "build", label: "Build Services", icon: Hammer, blurb: "Start building your new-age, AI-driven R&D and services team." },
    { key: "operate", label: "Operate Services", icon: Settings, blurb: "Run your GCC with confidence and drive organizational transformation." },
    { key: "transfer", label: "Transfer Services", icon: ArrowRightLeft, blurb: "Unlock a smooth handover with change management and end-to-end transfer." },
];

const data = {
    plan: [
        { icon: FileText, title: "Initial Planning", text: "About the entity, departments to be involved from customer side." },
        { icon: Layers, title: "Capacity Planning", text: "Plan capacity across departments and technologies. Head-count planning." },
        { icon: Users2, title: "Team Planning", text: "Plan team composition, leadership, the pyramid structure and reporting structure." },
        { icon: BookOpenCheck, title: "Knowledge Transfer Plan", text: "Plan the departments and key personnel involved in knowledge sharing and its structure." },
        { icon: Target, title: "Business Requirement Planning", text: "Setting up objectives and KPIs." },
        { icon: Building, title: "Workspace Design and Setup", text: "Boost collaboration with end-to-end GCC workspace solutions—from location selection to layout planning." },
    ],
    build: [

        { icon: Scale, title: "Legal Entity Set up", text: "Simplify your GCC launch with expert support on legal entity formation and regulatory approvals." },
        { icon: UserPlus, title: "Talent Acquisition", text: "Access top talent through our diverse sourcing ecosystem—internal pools, referrals, subcontractors." },
        { icon: HeartHandshake, title: "Talent Management", text: "Build a strong talent framework with our HRMS platform and expertise in performance, rewards, and recognition." },
        { icon: Briefcase, title: "Business Operations", text: "Streamline operations with expert-led financial, legal, tax, and compliance services." },

    ],
    operate: [
        { icon: LifeBuoy, title: "Talent Lifecycle Management", text: "Optimize HR processes and boost engagement with AI-driven solutions tailored for your GCC workforce." },
        { icon: Megaphone, title: "Customer Branding", text: "Attract and retain talent with a branding that reflects your unique culture." },
        { icon: ServerCog, title: "Always-on IT Support", text: "Ensure seamless operations with end-to-end network infrastructure services." },
        { icon: Shuffle, title: "Change Management", text: "Facilitate smooth organizational transformation by managing the people side of change." },
        {
            icon: GraduationCap, title: "People", bullets: [
                "Technology skill building: hands-on, expert-led training in digital and AI skills",
                "Future-fit talent development programs",
                "Mentorship program",
            ]
        },
        {
            icon: Cpu, title: "Technology and Innovation", bullets: [
                "Perform day-to-day operations",
                "Project / program monitoring",
                "Quality audits — reports & improvements",
                "Project governance",
                "Continuous improvement",
                "ROI realization",
            ]
        },
        { icon: TrendingUp, title: "Improve Maturity, Alignment and Efficiency", text: "Continuously mature your GCC with alignment reviews, efficiency benchmarks and improvement roadmaps." },
    ],
    transfer: [
        { icon: ClipboardList, title: "Transfer Planning", text: "Discuss and decide the transfer approach and align on the transfer success criteria." },
        { icon: FileSearch, title: "Knowledge Documentation", text: "Capture critical knowledge in standardized formats to enable smooth handovers." },
        { icon: Repeat2, title: "Stage-by-stage Transfer", text: "Start the transfer of process and people — and complete the transition." },
    ],
};

const fade = {
    hidden: { opacity: 0, y: 24 },
    show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const GccBuildServices = () => {
    const [params, setParams] = useSearchParams();
    const initial = params.get("tab") || "plan";
    const [active, setActive] = useState(tabs.some(t => t.key === initial) ? initial : "plan");

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const items = useMemo(() => data[active], [active]);
    const activeTab = tabs.find(t => t.key === active);

    const switchTab = (k) => {
        setActive(k);
        setParams({ tab: k }, { replace: true });
    };

    return (
        <div className="gbs-page">
            <section className="gbs-hero">
                <div className="gbs-hero__blob gbs-hero__blob--a" />
                <div className="gbs-hero__blob gbs-hero__blob--b" />
                <div className="gbs-hero__grid" />
                <motion.div className="gbs-hero__inner"
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
                    <Link to="/services/gcc-services" className="gbs-back"><ArrowLeft size={14} /> Back to GCC Services</Link>
                    <span className="gbs-hero__badge"><Sparkles size={14} /> AI Enabled GCC Services</span>
                    <h1 className="gbs-hero__title">Your Own <span>Offshore Company</span><br /> with Reduced Risk</h1>
                    <p className="gbs-hero__sub">Explore each stage of our BOT model — Plan, Build, Operate, Transfer.</p>
                </motion.div>
            </section>

            <section className="gbs-tabsWrap">
                <div className="gbs-tabs" role="tablist">
                    {tabs.map((t) => (
                        <button
                            key={t.key}
                            role="tab"
                            aria-selected={active === t.key}
                            className={`gbs-tab ${active === t.key ? "is-active" : ""}`}
                            onClick={() => switchTab(t.key)}
                        >
                            <t.icon size={16} />
                            <span>{t.label}</span>
                        </button>
                    ))}
                </div>
                <p className="gbs-tabs__blurb">
                    <activeTab.icon size={16} /> {activeTab.blurb}
                </p>
            </section>

            <section className="gbs-section">
                <AnimatePresence mode="wait">
                    <motion.div key={active} className="gbs-grid"
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.35 }}>
                        {items.map((it, i) => (
                            <motion.article key={it.title} className="gbs-card"
                                custom={i} initial="hidden" animate="show" variants={fade}>
                                <div className="gbs-card__icon"><it.icon size={22} /></div>
                                <h3>{it.title}</h3>
                                {it.text && <p>{it.text}</p>}
                                {it.bullets && (
                                    <ul className="gbs-card__list">
                                        {it.bullets.map(b => (
                                            <li key={b}><CheckCircle2 size={14} /><span>{b}</span></li>
                                        ))}
                                    </ul>
                                )}
                                <span className="gbs-card__corner" />
                                <span className="gbs-card__num">{String(i + 1).padStart(2, "0")}</span>
                            </motion.article>
                        ))}
                    </motion.div>
                </AnimatePresence>
            </section>
        </div>
    );
};

export default GccBuildServices;